import type { EvalOutcomeSnapshot, EvaluationRunRow, Subscore } from '@/pages/admin/types/EvaluationTypes'

const OUTCOME_JSON_PREFIX = 'OUTCOME_JSON:'

export type EvalMode = 'conversation' | 'vehicle'

export interface EvalJobView {
  id: string
  personaId: string
  personaLabel: string
  status: 'queued' | 'running' | 'success' | 'error'
  error?: string
  runId?: string
  trustScore?: number
  outcomeAttainment?: string | null
  escalated?: boolean
  appointmentScheduled?: boolean
  messageCount?: number
  customerName?: string
  customerId?: string
  judgeJustification?: string
  subscores?: Subscore[]
  vehicleRows?: EvaluationRunRow[]
}

export function formatPersonaName(personaId: string): string {
  return personaId
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

function parseOutcomeFromComments(comments: string | null | undefined): EvalOutcomeSnapshot | null {
  if (!comments?.includes(OUTCOME_JSON_PREFIX)) return null
  const idx = comments.indexOf(OUTCOME_JSON_PREFIX)
  try {
    return JSON.parse(comments.slice(idx + OUTCOME_JSON_PREFIX.length)) as EvalOutcomeSnapshot
  } catch {
    return null
  }
}

function parseOutcomeFromJustification(justification: string | null | undefined): string | null {
  const match = justification?.match(/^\[(pass|partial|fail|not_applicable)\]/i)
  return match?.[1]?.toLowerCase() ?? null
}

function extractTrustJustification(justification: string | null | undefined): string | undefined {
  if (!justification) return undefined
  const trustIdx = justification.indexOf('\n\nTrust: ')
  if (trustIdx >= 0) return justification.slice(trustIdx + 8).trim()
  return justification.replace(/^\[[^\]]+\]\s*[^\n]*\n\n?/, '').trim() || justification
}

function rowToConversationJob(row: EvaluationRunRow): EvalJobView {
  const outcome = parseOutcomeFromComments(row.judgeComments)
  return {
    id: row.id,
    personaId: row.personaId,
    personaLabel: formatPersonaName(row.personaId),
    status: 'success',
    runId: row.runId,
    trustScore: row.overallScore,
    outcomeAttainment: outcome?.outcomeAttainment ?? parseOutcomeFromJustification(row.judgeJustification),
    escalated: outcome?.escalated,
    appointmentScheduled: row.appointmentCompleted,
    messageCount: row.messageCount,
    customerName: row.customerName ?? undefined,
    customerId: row.customerId ?? undefined,
    judgeJustification: extractTrustJustification(row.judgeJustification),
    subscores: row.subscores.map(s => ({
      ...s,
      criterion: s.criterion,
    })),
  }
}

function rowsToVehicleJob(personaId: string, personaRows: EvaluationRunRow[]): EvalJobView {
  const general = personaRows.find(r => r.evalType === 'vehicle-eval-general')
  const recommendation = personaRows.find(r => r.evalType === 'vehicle-eval-recommendation')
  const primary = recommendation || general || personaRows[0]!
  const outcome = parseOutcomeFromComments(primary.judgeComments)

  return {
    id: primary.id,
    personaId,
    personaLabel: formatPersonaName(personaId),
    status: 'success',
    runId: primary.runId,
    trustScore: primary.overallScore,
    outcomeAttainment: outcome?.outcomeAttainment ?? parseOutcomeFromJustification(primary.judgeJustification),
    escalated: outcome?.escalated,
    appointmentScheduled: primary.appointmentCompleted,
    messageCount: primary.messageCount,
    customerName: primary.customerName ?? undefined,
    customerId: primary.customerId ?? undefined,
    judgeJustification: primary.judgeJustification ?? undefined,
    subscores: primary.subscores.map(s => ({
      ...s,
      criterion: s.criterion,
    })),
    vehicleRows: personaRows,
  }
}

/** Map persisted DB rows into the same shape used by the live results UI. */
export function rowsToEvalJobs(rows: EvaluationRunRow[], mode: EvalMode): EvalJobView[] {
  if (mode === 'vehicle') {
    const byPersona = new Map<string, EvaluationRunRow[]>()
    for (const row of rows) {
      if (!row.evalType.startsWith('vehicle-eval')) continue
      const list = byPersona.get(row.personaId) ?? []
      list.push(row)
      byPersona.set(row.personaId, list)
    }
    return Array.from(byPersona.entries()).map(([personaId, personaRows]) => rowsToVehicleJob(personaId, personaRows))
  }

  return rows.filter(r => r.evalType === 'conversation').map(rowToConversationJob)
}

export function inferEvalModeFromRows(rows: EvaluationRunRow[]): EvalMode {
  return rows.some(r => r.evalType.startsWith('vehicle-eval')) ? 'vehicle' : 'conversation'
}
